import { Button, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue, useSetAtom } from "jotai";
import {
	Archive,
	CircleArrowDown,
	Delete,
	Share2,
	SquarePen,
	Trash2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { DocumentKeys } from "web_myplann/Api/Document/DocumentKeys";
import { useDocumentDeleteMutation } from "web_myplann/Api/Document/useDocumentDeleteMutation";
import { useDocumentEditMutation } from "web_myplann/Api/Document/useDocumentEditMutation";
import {
	type DocumentDTO,
	DocumentLocalTypeEnum,
	getApiUrl,
	getDocumentIdContentV2Path,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DeleteDocumentDialog } from "web_myplann/Portfolio/Components/Document/DeleteDocumentDialog";
import { MoveDocumentDialog } from "web_myplann/Portfolio/Components/Document/MoveDocumentDialog";
import { downloadedDocumentsState } from "web_myplann/Portfolio/State/downloadedDocumentsAtom";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { MyPlannInput } from "web_myplann/Shared/Components/MyPlannInput";
import { sessionState } from "web_myplann/Shared/State/sessionAtom";
import { device } from "web_myplann/Utils/device";
import { HandleDocumentTypes } from "web_myplann/Utils/WebViewModel";

type Props = {
	item: DocumentDTO;
	onClose: () => void;
	activeTag?: string;
	isDownloaded?: boolean;
};

const Actions = {
	download: "download",
	removeDownload: "removeDownload",
	rename: "rename",
	share: "share",
	move: "move",
	delete: "delete",
} as const;

export const DocumentActionsDrawer = ({
	item,
	onClose,
	activeTag,
	isDownloaded,
}: Props) => {
	const theme = useTheme();
	const { t } = useResource();
	const queryClient = useQueryClient();

	const { token, tokenVerify, authorizationType } = useAtomValue(sessionState);
	const setDownloadedDocuments = useSetAtom(downloadedDocumentsState);

	const { mutate: renameDocument } = useDocumentEditMutation();
	const { mutate: deleteDocument } = useDocumentDeleteMutation();

	const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
	const [renameValue, setRenameValue] = useState(item.title);
	const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

	const handleDocumentDownload = async (
		action: (file: {
			filename: string;
			base64: string;
			mimeType?: string;
		}) => void,
	) => {
		const headers = new Headers();
		headers.set("Authorization", `Bearer ${token}`);
		headers.set("token-verify", tokenVerify);
		if (authorizationType) {
			headers.set("Authorization-type", authorizationType);
		}

		const response = await fetch(
			`${getApiUrl()}${getDocumentIdContentV2Path(item.id)}`,
			{
				headers,
			},
		);

		const blob = await response.blob();
		const reader = new FileReader();
		reader.onloadend = () => {
			let fileName: string | undefined;
			const result = reader.result;
			if (typeof result === "string") {
				const base64data = result.split(",")[1];
				const contentDisposition = response.headers.get("Content-Disposition");
				const mimeType = response.headers.get("Content-Type");
				if (contentDisposition) {
					const fileNameMatch =
						contentDisposition.match(/filename="?([^"]+)"?/);
					if (fileNameMatch?.[1]) {
						fileName = fileNameMatch[1];
					}
				}
				action({
					filename: fileName ?? item.title ?? "",
					base64: base64data,
					mimeType: mimeType ?? undefined,
				});
			}
		};
		reader.readAsDataURL(blob);
	};

	const handleAction = async (action: keyof typeof Actions) => {
		switch (action) {
			case Actions.download:
				handleDocumentDownload(async (file) => {
					const response = await device.saveAttachment(file);
					setDownloadedDocuments({ documents: response.payload.downloaded });
				});
				onClose();
				break;
			case Actions.share: {
				handleDocumentDownload((file) => {
					device.handleDocument({
						file,
						actionType: HandleDocumentTypes.SHARE,
						mimeType: file.mimeType,
					});
				});
				onClose();
				break;
			}
			case Actions.rename:
				setIsRenameDialogOpen(true);
				break;
			case Actions.delete:
				deleteDocument(item.id);
				onClose();
				break;
			case Actions.move:
				setIsMoveDialogOpen(true);
				break;
			case Actions.removeDownload: {
				const response = await device.removeAttachment(item.filename);
				if (response.payload.success) {
					setDownloadedDocuments({ documents: response.payload.downloaded });
				} else {
					toast.error(t(Resources.Common.somethingWentWrong));
				}
				onClose();
				break;
			}
		}
	};

	const handleRename = () => {
		renameDocument({
			requestContract: {
				title: renameValue,
			},
			id: item.id,
		});
		setIsRenameDialogOpen(false);
		onClose();
		if (activeTag) {
			queryClient.invalidateQueries({
				queryKey: DocumentKeys.list(activeTag),
			});
		}
	};

	return (
		<>
			<Stack px={2} pb={2} gap={1.2}>
				{!isDownloaded ? (
					<Stack
						direction="row"
						alignItems="center"
						gap={2}
						onClick={(e) => {
							e.stopPropagation();
							handleAction(Actions.download);
						}}
					>
						<CircleArrowDown color={theme.palette.primary.main} />
						<Typography>
							{t(
								Resources.Portfolio.List.DocumentActionsDrawer.Actions.download,
							)}
						</Typography>
					</Stack>
				) : (
					<Stack
						direction="row"
						alignItems="center"
						gap={2}
						onClick={(e) => {
							e.stopPropagation();
							handleAction(Actions.removeDownload);
						}}
					>
						<Delete color={theme.palette.primary.main} />
						<Typography>
							{t(
								Resources.Portfolio.List.DocumentActionsDrawer.Actions
									.removeDownload,
							)}
						</Typography>
					</Stack>
				)}
				{item.type === DocumentLocalTypeEnum.Local && (
					<Stack
						direction="row"
						alignItems="center"
						gap={2}
						onClick={(e) => {
							e.stopPropagation();
							handleAction(Actions.rename);
						}}
					>
						<SquarePen color={theme.palette.primary.main} />
						<Typography>
							{t(Resources.Portfolio.List.DocumentActionsDrawer.Actions.rename)}
						</Typography>
					</Stack>
				)}
				<Stack
					direction="row"
					alignItems="center"
					gap={2}
					onClick={(e) => {
						e.stopPropagation();
						handleAction(Actions.share);
					}}
				>
					<Share2 color={theme.palette.primary.main} />
					<Typography>
						{t(Resources.Portfolio.List.DocumentActionsDrawer.Actions.share)}
					</Typography>
				</Stack>
				<Stack
					direction="row"
					alignItems="center"
					gap={2}
					onClick={(e) => {
						e.stopPropagation();
						handleAction(Actions.move);
					}}
				>
					<Archive color={theme.palette.primary.main} />
					<Typography>
						{t(Resources.Portfolio.List.DocumentActionsDrawer.Actions.move)}
					</Typography>
				</Stack>
				{item.type === DocumentLocalTypeEnum.Local && (
					<Stack
						direction="row"
						alignItems="center"
						gap={2}
						onClick={(e) => {
							e.stopPropagation();
							setIsDeleteDialogOpen(true);
						}}
					>
						<Trash2 color={theme.palette.primary.main} />
						<Typography>
							{t(Resources.Portfolio.List.DocumentActionsDrawer.Actions.delete)}
						</Typography>
					</Stack>
				)}
			</Stack>

			<MyPlannDrawer
				open={isRenameDialogOpen}
				onClose={() => {
					setIsRenameDialogOpen(false);
					onClose();
				}}
				title={t(Resources.Portfolio.List.DocumentActionsDrawer.Actions.rename)}
			>
				<Stack px={2} pb={2} gap={1.2}>
					<MyPlannInput
						label={t(
							Resources.Portfolio.List.DocumentActionsDrawer.Rename.fileRename,
						)}
						value={renameValue}
						onChange={(e) => setRenameValue(e.target.value)}
					/>
					<Button onClick={handleRename}>{t(Resources.Common.save)}</Button>
				</Stack>
			</MyPlannDrawer>
			<MyPlannDrawer
				open={isMoveDialogOpen}
				onClose={() => {
					setIsMoveDialogOpen(false);
					onClose();
				}}
				title={t(
					Resources.Portfolio.List.DocumentActionsDrawer.fileDrawerTitle,
				)}
			>
				<MoveDocumentDialog
					item={item}
					activeTag={activeTag}
					onClose={() => {
						setIsMoveDialogOpen(false);
						onClose();
					}}
				/>
			</MyPlannDrawer>
			<DeleteDocumentDialog
				document={item}
				open={isDeleteDialogOpen}
				onClose={(result) => {
					setIsDeleteDialogOpen(false);
					onClose();
					if (result) {
						handleAction(Actions.delete);
					}
				}}
			/>
		</>
	);
};
